import type { AthletePaymentAssignment, LessonPlanRole } from "../object-actions/types/types";
import { canAccessPreAssessment } from "../subscriptions/purposeEntitlements";
import {
    type OrgWaiverConfig,
    waiverFileForRole,
} from "./waiver";

/**
 * Query params that should survive every hop of the auth + onboarding flow
 * (signup -> verify email -> login -> onboarding -> destination). They encode
 * where the user was trying to go before we interrupted them with auth.
 */
export const RETURN_PARAMS = ["redirect", "next", "athlete_invite_accepted"] as const;

const RETURN_STORAGE_KEY = "smsp_onboarding_return";

export const PARTICIPATION_ROLES: LessonPlanRole[] = ["athlete", "coach", "parent"];

/** @deprecated import from utils/waiver */
export { waiverFileForRole } from "./waiver";

export function hasAcceptedWaiverForRole(
    user: { accepted_waivers?: string[] } | null | undefined,
    role: LessonPlanRole | string | null | undefined,
    orgOrAthleteWaiver?: OrgWaiverConfig | string | null,
): boolean {
    if (!user || !role) return false;
    const org: OrgWaiverConfig | null =
        typeof orgOrAthleteWaiver === "string"
            ? { waiver_athlete: orgOrAthleteWaiver }
            : orgOrAthleteWaiver ?? null;
    const file = waiverFileForRole(role, org);
    return (user.accepted_waivers ?? []).includes(file);
}

export function assignmentWaiverComplete(
    assignment: Pick<AthletePaymentAssignment, "waiver_required"> | null | undefined,
    user: { accepted_waivers?: string[] } | null | undefined,
    myRoles: string[] | null | undefined,
    org?: OrgWaiverConfig | null,
): boolean {
    if (assignment && typeof assignment.waiver_required === "boolean") {
        return !assignment.waiver_required;
    }
    if (!user || !myRoles?.length) return true;
    for (const role of myRoles) {
        if (!PARTICIPATION_ROLES.includes(role as LessonPlanRole)) continue;
        if (!hasAcceptedWaiverForRole(user, role, org)) return false;
    }
    return true;
}
export function getReturnParams(search: string): URLSearchParams {
    const incoming = new URLSearchParams(search);
    const out = new URLSearchParams();
    RETURN_PARAMS.forEach((key) => {
        const value = incoming.get(key);
        if (value) out.set(key, value);
    });
    return out;
}

/** "?redirect=..." or "" for the persisted params found in `search`. */
export function getReturnSearch(search: string): string {
    const params = getReturnParams(search);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

export function storeReturnParams(search: string): void {
    if (typeof window === "undefined") return;
    const qs = getReturnParams(search).toString();
    try {
        if (qs) {
            window.sessionStorage.setItem(RETURN_STORAGE_KEY, qs);
        }
    } catch {
        // ignore
    }
}

export function loadStoredReturnSearch(): string {
    if (typeof window === "undefined") return "";
    try {
        const qs = window.sessionStorage.getItem(RETURN_STORAGE_KEY);
        return qs ? `?${qs}` : "";
    } catch {
        return "";
    }
}

export function clearStoredReturnParams(): void {
    if (typeof window === "undefined") return;
    try {
        window.sessionStorage.removeItem(RETURN_STORAGE_KEY);
    } catch {
        // ignore
    }
}

export function withReturn(path: string, returnSearch: string): string {
    const ret = new URLSearchParams(returnSearch.replace(/^\?/, ""));
    if ([...ret.keys()].length === 0) return path;

    const [base, ownQuery = ""] = path.split("?");
    const merged = new URLSearchParams(ownQuery);
    ret.forEach((value, key) => {
        if (!merged.has(key)) merged.set(key, value);
    });
    const qs = merged.toString();
    return qs ? `${base}?${qs}` : base;
}

export function consumeReturnTarget(returnSearch: string): string | null {
    const params = new URLSearchParams(returnSearch.replace(/^\?/, ""));
    const target = params.get("redirect") || params.get("next");
    if (!target) return null;
    if (!target.startsWith("/") || target.startsWith("//")) return null;
    return target;
}

export function assignmentOnboardingPath(assignmentId: number | string): string {
    return `/assignments/${assignmentId}/onboarding`;
}

export function assignmentProgramProgressPath(assignmentId: number | string): string {
    return `/program-progress/${assignmentId}`;
}

export type OnboardingStepKey =
    | "role"
    | "waiver"
    | "profile"
    | "athlete"
    | "guardian"
    | "coaching"
    | "assessment";

export interface OnboardingStepCta {
    label: string;
    to?: string;
    action?: "open_waiver" | "select_role" | "open_invite_athlete" | "open_invite_guardian";
    disabled?: boolean;
}

/** Coach or guardian on the assignment, or AI coaching mode enabled. */
export function assignmentHasCoachingSupport(
    assignment: Pick<
        AthletePaymentAssignment,
        "coaches" | "parents" | "coaching_mode" | "coach_count" | "parent_count"
    > | null | undefined,
): boolean {
    if (!assignment) return false;
    if (assignment.coaching_mode === "ai") return true;
    if ((assignment.coaches?.length ?? 0) > 0) return true;
    if ((assignment.parents?.length ?? 0) > 0) return true;
    if ((assignment.coach_count ?? 0) > 0) return true;
    if ((assignment.parent_count ?? 0) > 0) return true;
    return false;
}

export interface OnboardingStep {
    key: OnboardingStepKey;
    label: string;
    description: string;
    done: boolean;
    applicable: boolean;
    cta?: OnboardingStepCta;
    /**
     * When true and the step is done: show the checkmark but keep the CTA
     * visible and skip strikethrough (e.g. profile — still editable).
     */
    keepInteractiveWhenDone?: boolean;
    /**
     * Shown in the checklist but ignored for completion / percent / current step.
     * E.g. coaches may invite a guardian alongside the required athlete invite.
     */
    optional?: boolean;
}

export interface OnboardingState {
    steps: OnboardingStep[];
    currentStep: OnboardingStep | null;
    isComplete: boolean;
    percent: number;
    /** Participation role on this assignment (athlete/coach/parent), if any. */
    participationRole: LessonPlanRole | null;
    isPayer: boolean;
}

export function getParticipationRole(myRoles?: string[] | null): LessonPlanRole | null {
    if (!myRoles?.length) return null;
    for (const role of PARTICIPATION_ROLES) {
        if (myRoles.includes(role)) return role;
    }
    return null;
}

export function isPayerOnAssignment(myRoles?: string[] | null): boolean {
    return !!myRoles?.includes("payer");
}

interface ComputeArgs {
    user: any;
    assignment: AthletePaymentAssignment | null;
    assignmentId?: number | null;
    orgWaiver?: OrgWaiverConfig | null;
    /** @deprecated use orgWaiver */
    orgAthleteWaiver?: string | null;
    applyRoleLabels?: (text: string) => string;
    /** Pending coach/guardian invite exists (not yet accepted). */
    hasPendingSupportInvite?: boolean;
    /** Pending guardian (parent) invite exists (not yet accepted). */
    hasPendingGuardianInvite?: boolean;
}

/**
 * Assignment-scoped onboarding checklist. Roles come from assignment.my_roles,
 * not Django groups or org membership.
 */
export function computeOnboardingSteps({
    user,
    assignment,
    orgWaiver,
    orgAthleteWaiver,
    applyRoleLabels = (t) => t,
    hasPendingSupportInvite = false,
    hasPendingGuardianInvite = false,
}: ComputeArgs): OnboardingState {
    const orgConfig: OrgWaiverConfig | null =
        orgWaiver ?? (orgAthleteWaiver ? { waiver_athlete: orgAthleteWaiver } : null);
    const myRoles = assignment?.my_roles ?? [];
    const participationRole = getParticipationRole(myRoles);
    const isPayer = isPayerOnAssignment(myRoles);
    const isAthlete = participationRole === "athlete";
    const isCoach = participationRole === "coach";
    const needsAthleteSlot = participationRole === "coach" || participationRole === "parent";
    const hasAthleteOnAssignment = !!(assignment?.athlete_id ?? assignment?.athlete?.id);
    const hasGuardianOnAssignment =
        (assignment?.parents?.length ?? 0) > 0 || (assignment?.parent_count ?? 0) > 0;
    const coachingReady = assignmentHasCoachingSupport(assignment);
    const athleteLabel = applyRoleLabels("Athlete");
    const coachLabel = applyRoleLabels("Coach");
    const guardianLabel = applyRoleLabels("Guardian");
    const id = assignment?.paymentassignment_id;
    const waiverDone = assignmentWaiverComplete(assignment, user, myRoles, orgConfig);

    let coachingDescription: string;
    if (coachingReady) {
        coachingDescription =
            assignment?.coaching_mode === "ai"
                ? "Your program will run with an AI coach."
                : `A ${coachLabel.toLowerCase()} or ${guardianLabel.toLowerCase()} is on this assignment.`;
    } else if (!waiverDone) {
        coachingDescription = `Sign your waiver first, then invite a ${coachLabel.toLowerCase()} or ${guardianLabel.toLowerCase()}, or start with an AI coach.`;
    } else if (hasPendingSupportInvite) {
        coachingDescription =
            `Waiting for your invite to be accepted. Once they join—or you start with an AI coach—you can take the assessment.`;
    } else {
        coachingDescription =
            `Invite a ${coachLabel.toLowerCase()} or ${guardianLabel.toLowerCase()} (they must accept), or start with an AI coach to continue.`;
    }

    const steps: OnboardingStep[] = [
        {
            key: "role",
            label: "Choose your role",
            description: "You are the payer for this assignment. Select how you will participate.",
            applicable: isPayer && !participationRole,
            done: !!participationRole,
            cta: { label: "Choose role", action: "select_role" },
        },
        {
            key: "waiver",
            label: "Accept your waiver",
            description: "Review and sign the agreement required for your role.",
            applicable: !!participationRole,
            done: waiverDone,
            cta: { label: "Review & sign", action: "open_waiver" },
        },
        {
            key: "profile",
            label: "Complete your profile",
            description: "Add your name and date of birth so reports are accurate.",
            applicable: isAthlete,
            done: !!user?.profile_complete,
            keepInteractiveWhenDone: true,
            cta: {
                label: "Edit profile",
                to: id
                    ? `/my-profile/edit?next=${encodeURIComponent(assignmentOnboardingPath(id))}`
                    : "/my-profile/edit",
            },
        },
        {
            key: "athlete",
            label: `Invite your ${athleteLabel.toLowerCase()}`,
            description: waiverDone
                ? `Connect the ${athleteLabel.toLowerCase()} who will take the assessment.`
                : `Sign your waiver first, then invite your ${athleteLabel.toLowerCase()}.`,
            applicable: needsAthleteSlot,
            done: hasAthleteOnAssignment,
            cta: {
                label: `Add ${athleteLabel.toLowerCase()}`,
                action: "open_invite_athlete",
                disabled: !waiverDone,
            },
        },
        {
            key: "guardian",
            label: `Invite a ${guardianLabel.toLowerCase()}`,
            description: waiverDone
                ? `Optionally invite a ${guardianLabel.toLowerCase()} so they can follow progress and talking points.`
                : `Sign your waiver first, then you can invite a ${guardianLabel.toLowerCase()}.`,
            applicable: isCoach,
            done: hasGuardianOnAssignment || hasPendingGuardianInvite,
            optional: true,
            keepInteractiveWhenDone: true,
            cta: {
                label: `Add ${guardianLabel.toLowerCase()}`,
                action: "open_invite_guardian",
                disabled: !waiverDone,
            },
        },
        {
            key: "coaching",
            label: "Set up your coaching",
            description: coachingDescription,
            applicable: isAthlete,
            done: coachingReady,
            // Actions rendered by CoachingStepActions in the checklist (invite + AI).
        },
        {
            key: "assessment",
            label: "Take your assessment",
            description: coachingReady
                ? "Complete your confidence assessment to unlock your program."
                : "Finish coaching setup first—wait for an invite acceptance or start with an AI coach.",
            applicable: isAthlete,
            done: !!assignment?.pre_assessment_submitted_at,
            cta:
                assignment?.assessment_id &&
                    canAccessPreAssessment(assignment) &&
                    waiverDone &&
                    coachingReady
                    ? {
                        label: assignment?.pre_submission_response_count
                            ? "Resume assessment"
                            : "Take assessment",
                        to: `/assessments/${assignment.assessment_id}`,
                    }
                    : undefined,
        },
    ];

    const applicable = steps.filter((s) => s.applicable);
    // Optional steps appear in the checklist but do not gate completion.
    const required = applicable.filter((s) => !s.optional);
    const doneCount = required.filter((s) => s.done).length;
    const isComplete = required.length > 0 && doneCount === required.length;
    const currentStep = required.find((s) => !s.done) || null;
    const percent = required.length === 0 ? 100 : Math.round((doneCount / required.length) * 100);

    return { steps, currentStep, isComplete, percent, participationRole, isPayer };
}
